import React, {useState, useEffect} from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {motion, AnimatePresence} from 'framer-motion';

const {FiZap, FiMenu, FiX, FiChevronDown, FiMail} = FiIcons;

const NewHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navLinks = [
    {name: 'Features', href: '#features'},
    {name: 'How You Start', href: '#how-you-start'},
    {name: 'Pricing', href: '#pricing'},
    {name: 'FAQ', href: '#faq'}
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('#') && href.length > 1) {
      const targetId = href.substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        window.scrollTo({
          top: targetElement.offsetTop - 80,
          behavior: 'smooth'
        });
      }
    }
    setIsMenuOpen(false);
  };

  const handleContactClick = () => {
    window.location.href = 'mailto:hey@yiksy.com';
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-lg shadow-lg py-2 sm:py-3' 
        : 'bg-transparent py-4 sm:py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <motion.div 
            className="flex items-center space-x-2 sm:space-x-3"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.5}}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden bg-white shadow-lg border-2 border-gray-100 flex items-center justify-center">
              <img 
                src="/logo.svg" 
                alt="Yiksy Logo" 
                className="w-full h-full object-contain" 
              />
            </div>
            <span className="text-xl sm:text-2xl tracking-tight">
              <span className="font-bold text-text">Yiksy</span>
            </span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8 lg:space-x-10">
            <nav className="flex items-center space-x-8 lg:space-x-10 mr-6 lg:mr-8">
              {navLinks.map((link, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleNavClick(link.href)}
                  className="text-text-light hover:text-primary transition-colors font-medium relative group text-sm lg:text-base cursor-pointer"
                  initial={{y: -10, opacity: 0}}
                  animate={{y: 0, opacity: 1}}
                  transition={{duration: 0.3, delay: index * 0.1}}
                >
                  {link.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full"></span>
                </motion.button>
              ))}
            </nav>

            <motion.button 
              className="bg-primary text-white px-6 lg:px-8 py-3 lg:py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg flex items-center group btn-3d text-sm lg:text-base"
              whileHover={{scale: 1.03}}
              whileTap={{scale: 0.97}}
              initial={{opacity: 0, scale: 0.9}}
              animate={{opacity: 1, scale: 1}}
              transition={{duration: 0.3, delay: 0.4}}
              onClick={handleContactClick}
            >
              Contact Us
              <SafeIcon icon={FiMail} className="ml-2 group-hover:scale-110 transition-transform text-sm" />
            </motion.button>
          </div>

          {/* Mobile menu button */}
          <motion.button 
            className="md:hidden text-text p-2 rounded-lg hover:bg-gray-100 transition-colors touch-manipulation"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{scale: 0.9}}
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl sm:text-2xl" />
          </motion.button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="md:hidden bg-white shadow-xl absolute top-full left-0 right-0 border-t border-gray-100"
            initial={{opacity: 0, height: 0}}
            animate={{opacity: 1, height: 'auto'}}
            exit={{opacity: 0, height: 0}}
            transition={{duration: 0.3}}
          >
            <nav className="flex flex-col px-4 py-6">
              {navLinks.map((link, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleNavClick(link.href)}
                  className="text-text hover:text-primary transition-colors font-medium py-4 border-b border-gray-100 last:border-b-0 flex justify-between items-center touch-manipulation text-left"
                  initial={{x: -20, opacity: 0}}
                  animate={{x: 0, opacity: 1}}
                  transition={{duration: 0.2, delay: index * 0.1}}
                >
                  <span className="text-lg">{link.name}</span>
                  <SafeIcon icon={FiChevronDown} className="text-text-light transform -rotate-90" />
                </motion.button>
              ))}

              <motion.button 
                className="bg-primary text-white px-6 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors w-full mt-6 flex items-center justify-center shadow-lg touch-manipulation"
                onClick={() => {
                  setIsMenuOpen(false);
                  handleContactClick();
                }}
                initial={{y: 20, opacity: 0}}
                animate={{y: 0, opacity: 1}}
                transition={{duration: 0.3, delay: 0.4}}
              >
                Contact Us
                <SafeIcon icon={FiMail} className="ml-2" />
              </motion.button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default NewHeader;